'use client'

import { cn } from '@/lib/utils'
import { useStoreI18n } from '@/store'
import {
  Typo3SectionSpotSanityAccomodation,
  Typo3SectionSpotSanityCulture,
  Typo3SectionSpotSanityEatDrink,
  Typo3SectionSpotSanityEventList,
  Typo3SectionSpotSanityFreetime,
  Typo3SectionSpotSanityNature,
  Typo3SectionSpotSanityService,
  Typo3SectionSpotSanityShopping,
  Typo3SectionSpotSanitySport,
  Typo3SectionSpotSanitySportServices
} from '@/types'
import {
  OpeningTime,
  Typo3EatDrinkDetailItem
} from '@/types/typo3/sections/spotsanity-eatdrink/detail'
import { Richtext } from '@/ui/components'
import { memo } from 'react'

function transformTimeDataToOpeningArray(
  timeData: Typo3EatDrinkDetailItem['time']
): {
  title: string
  dates: { day: string; opening: string }[]
  comment: string
  validThrough: string
  validFrom: string
}[] {
  const D = useStoreI18n.use.dictionary()

  const daysOfWeek = [
    D['fe.date.day.1'], // Monday
    D['fe.date.day.2'], // Tuesday
    D['fe.date.day.3'], // Wednesday
    D['fe.date.day.4'], // Thursday
    D['fe.date.day.5'], // Friday
    D['fe.date.day.6'], // Saturday
    D['fe.date.day.0'] // Sunday
  ]

  const openingArray: {
    title: string
    dates: { day: string; opening: string }[]
    comment: string
    validThrough: string
    validFrom: string
  }[] = []

  const formatOpeningTimes = (openingTimes?: OpeningTime[]): string => {
    if (closed) {
      return D['fe.plugin.closed']
    } else if (!closed && !openingTimes) {
      return D['fe.plugin.open']
    }
    return openingTimes
      ? openingTimes
          .map(
            (time) =>
              `${time.opens} - ${time.closes} ${D['fe.plugin.openings.h']}`
          )
          .join(' | ')
      : D['fe.plugin.closed']
  }

  // Verarbeite `specialHours`, falls vorhanden
  timeData?.specialHours?.forEach((specialHour) => {
    const specialDays = specialHour.weekdays
      .map((day) => {
        const openingString = formatOpeningTimes(day.openingTimes)
        return day.isoWeekday.map((weekday) => ({
          day: daysOfWeek[weekday - 1],
          opening: day.closed ? D['fe.plugin.closed'] : openingString
        }))
      })
      .flat()

    const sortedDays = specialDays.sort(
      (a, b) => daysOfWeek.indexOf(a.day) - daysOfWeek.indexOf(b.day)
    )

    openingArray.push({
      title: '',
      dates: sortedDays,
      comment: specialHour.weekdays[0]?.comment || '',
      validFrom: specialHour.validFrom || '',
      validThrough: specialHour.validThrough || ''
    })
  })

  // Verarbeite `regularHours`, falls vorhanden
  const weekSchedule = daysOfWeek.map((day) => ({
    day,
    opening: D['fe.plugin.closed']
  }))

  timeData?.regularHours?.forEach((regularHour) => {
    const openingString = formatOpeningTimes(regularHour.openingTimes)
    regularHour.isoWeekday.forEach((dayIndex) => {
      // Passe den Index an, um unser daysOfWeek-Array (0-6) zu verwenden
      const adjustedIndex = dayIndex - 1
      weekSchedule[adjustedIndex] = {
        day: daysOfWeek[adjustedIndex],
        opening: regularHour.closed ? D['fe.plugin.closed'] : openingString
      }
    })
  })

  if (timeData?.regularHours?.length) {
    openingArray.push({
      title: D['fe.plugin.openings.regularOpeningHours'],
      dates: weekSchedule,
      comment: timeData.regularHours[0]?.comment || '',
      validFrom: '',
      validThrough: ''
    })
  }

  return openingArray
}

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  data:
    | Typo3SectionSpotSanityNature
    | Typo3SectionSpotSanitySport
    | Typo3SectionSpotSanityEatDrink
    | Typo3SectionSpotSanityAccomodation
    | Typo3SectionSpotSanityCulture
    | Typo3SectionSpotSanityService
    | Typo3SectionSpotSanityShopping
    | Typo3SectionSpotSanityFreetime
    | Typo3SectionSpotSanitySportServices
    | Typo3SectionSpotSanityEventList
}

const DetailHours = ({ data }: Props) => {
  const getVariant = (): 'event' | 'default' => {
    switch (data.type) {
      case 'spotsanity_eventlist':
        return 'event'
      default:
        return 'default'
    }
  }

  if (getVariant() === 'event') {
    return null
  }

  // @ts-expect-error - time is typed
  const time = data?.content?.item?.time
  //const regularHours = time?.weekOpenings
  const specialHours = time?.specialHours

  // Function to transform the time data into the desired format
  function transformDate(dateString: string): string {
    const [year, month, day] = dateString.split('-')
    return `${day}.${month}.${year}`
  }

  // function isDateInRange(
  //   date: string,
  //   validFrom: string,
  //   validThrough: string
  // ): boolean {
  //   const checkDate = new Date(date)
  //   const fromDate = new Date(validFrom)
  //   const toDate = new Date(validThrough)
  //   return checkDate >= fromDate && checkDate <= toDate
  // }

  const wrapClass = cn(
    'gap-xl grid grid-cols-2 max-sm:grid-cols-1',
    specialHours.length > 0 && ''
  )

  const TIME = transformTimeDataToOpeningArray(
    time as Typo3EatDrinkDetailItem['time']
  )

  if (TIME.length === 0) {
    return null
  }

  return (
    <div className={wrapClass}>
      {TIME.map((time, i) => (
        <div key={i?.toString()} className="flex flex-col">
          {time.title && <p className="mb-sm font-bold">{time.title} </p>}

          {time.validFrom && time.validThrough && (
            <div className="mb-sm font-bold">
              {`${transformDate(time.validFrom)} - ${transformDate(time.validThrough)}`}
            </div>
          )}
          {time.dates.map((date, k) => (
            <div key={k?.toString()}>{`${date.day}: ${date.opening}`}</div>
          ))}
          {time.comment && (
            <Richtext className="mt-xl text-neutral-400" text={time.comment} />
          )}
        </div>
      ))}
    </div>
  )
}

export default memo(DetailHours)
